// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useMemo } from 'react';

import {
  type FacilityId,
  type WorkCell,
  type ShopConfiguration,
  useEvolvePaginatedGet,
  useEvolvePatch,
} from '@evolvemep/api-ts';
import { Loader } from '@mantine/core';
import type { GridApi } from 'ag-grid-enterprise';

import { detailTable } from 'helpers/ag-grid/DetailTables/detailTable';
import { refreshGrid } from 'helpers/ag-grid/helpers';
import { useServerSideGrid } from 'helpers/ag-grid/useServerSideGrid';
import { isNil, isNotNil } from 'helpers/isNotNil';

import { getWorkCellsColumnDef, getWorkCellTaskTypeColDef } from './columnDefs';

const getRowAtIndex = (api: GridApi<WorkCell>, index: number) => {
  let i = index + 1;
  let row;
  do {
    i -= 1;
    row = api.getDisplayedRowAtIndex(i);
  } while (row?.detail);
  return row;
};

export const useWorkCellsGrid = (
  facilityId: FacilityId,
  viewing: 'active' | 'draft',
  shopConfiguration: ShopConfiguration | undefined,
  splitViewIsOpen: boolean,
) => {
  const { fetchPage, abortAll, setDefaultOpts } = useEvolvePaginatedGet(`shop/workCell`);

  useEffect(() => {
    if (isNotNil(shopConfiguration?.shopConfigurationId)) {
      setDefaultOpts({
        defaultConfig: {
          params: {
            facilityId,
            excludeUnassigned: true,
            orderBy: 'sortOrder:asc',
            shopConfigurationId: shopConfiguration.shopConfigurationId,
          },
        },
      });
    }
  }, [facilityId, setDefaultOpts, shopConfiguration?.shopConfigurationId]);

  const { fetchPage: fetchPageWorkCellTaskType } = useEvolvePaginatedGet('shop/workCellTaskType');

  const colDef = useMemo(
    () =>
      getWorkCellsColumnDef({
        shopConfiguration,
        isDraftView: viewing === 'draft',
        splitViewIsOpen,
      }),
    [shopConfiguration, viewing, splitViewIsOpen],
  );
  const { apiCall: reSortWorkCell, loading: sorting } = useEvolvePatch('shop/workCell/:workCellId/prioritize');

  const workCellTaskTypeColDef = useMemo(() => getWorkCellTaskTypeColDef({ canEdit: viewing === 'draft' }), [viewing]);
  const gridProps = useServerSideGrid({
    colDef,
    fetchPage,
    abortAll,
    rowId: 'workCellId',
    tableName: 'work-cells',
    detailTables: [
      detailTable({
        title: 'Tasks',
        colDef: workCellTaskTypeColDef,
        configMapper: ({ workCellId }: WorkCell) => ({
          params: {
            workCellId,
          },
        }),
        fetchPage: fetchPageWorkCellTaskType,
        icon: 'WorkCellTask',
        hideHeader: true,
        autoExpand: true,
      }),
    ],
  });
  const agGridProps: typeof gridProps.agGridProps = useMemo(
    () => ({
      ...gridProps.agGridProps,
      onRowDragLeave: ({ api }) => {
        api.setRowDropPositionIndicator(null);
      },
      onRowDragMove: ({ api, overIndex }) => {
        if (overIndex === -1) {
          api.setRowDropPositionIndicator({
            row: api.getDisplayedRowAtIndex(api.getLastDisplayedRowIndex()),
            dropIndicatorPosition: 'below',
          });
        } else {
          api.setRowDropPositionIndicator({ row: getRowAtIndex(api, overIndex), dropIndicatorPosition: 'above' });
        }
      },
      onRowDragEnd: ({ overIndex, api, node }) => {
        api.setRowDropPositionIndicator(null);
        const workCellId = node.data?.workCellId;
        if (isNil(workCellId)) return;

        const hoveredRow = getRowAtIndex(api, overIndex);
        const previousRow = isNotNil(hoveredRow)
          ? getRowAtIndex(api, (hoveredRow.rowIndex ?? 0) - 1)
          : api.getDisplayedRowAtIndex(api.getLastDisplayedRowIndex());

        if (hoveredRow?.data?.workCellId === workCellId || previousRow?.data?.workCellId === workCellId) {
          return;
        }
        void reSortWorkCell(
          {},
          {
            url: `shop/workCell/${workCellId}/prioritize`,
            params: {
              after: previousRow?.data?.workCellId,
            },
          },
        ).finally(() => refreshGrid(gridProps.gridApi));
      },
      // oxlint-disable-next-line typescript/no-unsafe-member-access
      rowDragText: ({ rowNode }) => rowNode?.data?.workCellName,
      loadingOverlayComponent: () => <Loader type="bars" />,
    }),
    [gridProps, reSortWorkCell],
  );

  return {
    ...gridProps,
    agGridProps,
    sorting,
  };
};
